import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { ColumnChooserModel } from '../models/common/ColumnChooserModel';
import { genislikleriSifirla } from '../helpers/kolonGenislik';
import { birakmaSirasi, kenarKaydirma, tamListeyeUygula, yerlestir } from '../helpers/kolonSirasi';
import Box from './Box';
import CustomTextInput from './CustomTextInput';

interface Props {
    columns: ColumnChooserModel[]
    setColumns: React.Dispatch<React.SetStateAction<ColumnChooserModel[]>>
    pageName: string,
    pageVersion: string,
    getColumns: () => ColumnChooserModel[]
}

const ColumnChooser = ({ columns, setColumns, pageName, pageVersion, getColumns }: Props) => {
    const [searchValue, setSearchValue] = useState<string | undefined>(undefined);

    /*
     * Sürükle-bırak sıralama (16.10.2026, Burak): kolon tutamaçtan (☰) tutulup tek seferde istenen yere taşınır.
     * Fare listenin üst/alt kenarına yaklaşınca liste kendiliğinden kayar; bırakılacak yer her karede satırların
     * o anki konumuna göre hesaplandığı için kayan listede de doğru yere düşer. Aramayla süzülmüş listede de
     * kolon, görünen yeni komşusunun yanına taşınır.
     */
    const [surukleme, setSurukleme] = useState<{ ad: string, sira: string[] } | null>(null);
    const kapRef = useRef<HTMLDivElement>(null);
    const satirlar = useRef<Record<string, HTMLDivElement | null>>({});
    const durum = useRef<{ ad: string, sira: string[], y: number, kare: number } | null>(null);
    const dinleyicileriKaldir = useRef<(() => void) | null>(null);

    const gorunenler = columns.filter(x => searchValue ? x.title.replaceAll(' ', '').toLowerCase().includes(searchValue.replaceAll(' ', '').toLowerCase()) : true);

    const siraKaydet = (adlar: string[]) => {
        var l = adlar.map(ad => columns.find(x => x.name == ad)!).filter(x => !!x);

        for (var i = 0; i < l.length; i++) {
            l[i].order = i + 1;
        }

        setColumns(l)

        localStorage.setItem(pageName + "-" + pageVersion + "-filters", JSON.stringify(l))
    }

    // Her karede: kenardaysa listeyi kaydır, sonra bırakılacak yeri satırların güncel konumundan bul.
    const kare = () => {
        const d = durum.current;
        const kap = kapRef.current;

        if (!d || !kap)
            return;

        const kutu = kap.getBoundingClientRect();
        const adim = kenarKaydirma(d.y, kutu.top, kutu.bottom);

        if (adim != 0)
            kap.scrollTop += adim;

        siraGuncelle();
        d.kare = requestAnimationFrame(kare);
    }

    // Bırakılacak yer: sürüklenen dışındaki satırların o anki orta noktalarından.
    const siraGuncelle = () => {
        const d = durum.current;

        if (!d)
            return;

        const digerleri = d.sira.filter(x => x != d.ad);
        const ortalar = digerleri.map(ad => {
            const r = satirlar.current[ad]?.getBoundingClientRect();
            return r ? r.top + r.height / 2 : Number.NEGATIVE_INFINITY;
        });
        const yeni = yerlestir(d.sira, d.ad, birakmaSirasi(ortalar, d.y));

        if (yeni.join('|') != d.sira.join('|')) {
            d.sira = yeni;
            setSurukleme({ ad: d.ad, sira: yeni });
        }
    }

    const hareket = (e: PointerEvent) => {
        if (durum.current)
            durum.current.y = e.clientY;
    }

    const bitir = (uygula: boolean) => {
        const d = durum.current;

        dinleyicileriKaldir.current?.();

        if (!d)
            return;

        setSurukleme(null);

        if (!uygula)
            return;

        const tumu = columns.map(x => x.name);
        const yeni = tamListeyeUygula(tumu, d.sira, d.ad);

        if (yeni.join('|') != tumu.join('|'))
            siraKaydet(yeni);
    }

    const birak = (e: PointerEvent) => {
        // Hızlı bırakmada son kare beklenmeden yer bırakılan noktaya göre hesaplanır.
        hareket(e);
        siraGuncelle();

        // Liste dışında bırakılınca oluşan tıklama menüyü kapatmasın.
        const yut = (e: MouseEvent) => e.stopPropagation();
        window.addEventListener('click', yut, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', yut, { capture: true }), 0);
        bitir(true);
    }

    const vazgec = () => bitir(false);

    const suruklemeyeBasla = (e: React.PointerEvent, ad: string) => {
        if (e.button != 0)
            return;

        e.preventDefault();
        e.stopPropagation();

        const sira = gorunenler.map(x => x.name);
        durum.current = { ad, sira, y: e.clientY, kare: 0 };
        setSurukleme({ ad, sira });
        document.body.classList.add('kolon-secici-surukleniyor');

        window.addEventListener('pointermove', hareket);
        window.addEventListener('pointerup', birak);
        window.addEventListener('pointercancel', vazgec);
        durum.current.kare = requestAnimationFrame(kare);

        // Kaldırma, eklenen fonksiyonların kendisini tutar (sonraki çizimlerde yenileri oluşur).
        dinleyicileriKaldir.current = () => {
            window.removeEventListener('pointermove', hareket);
            window.removeEventListener('pointerup', birak);
            window.removeEventListener('pointercancel', vazgec);
            document.body.classList.remove('kolon-secici-surukleniyor');

            if (durum.current)
                cancelAnimationFrame(durum.current.kare);

            durum.current = null;
            dinleyicileriKaldir.current = null;
        };
    }

    // Ekran kapanırsa yarım kalan sürükleme temizlenir.
    useEffect(() => () => dinleyicileriKaldir.current?.(), []);

    const listelenen = surukleme
        ? surukleme.sira.map(ad => columns.find(x => x.name == ad)!).filter(x => !!x)
        : gorunenler;

    const setColumnVisibility = (name: string) => {
        var c = columns.map(function (x) {
            if (x.name == name) {
                x.isVisible = !x.isVisible;
            }

            return x;
        });

        setColumns(c);

        localStorage.setItem(pageName + "-" + pageVersion + "-filters", JSON.stringify(c))
    }

    const selectOrDeselectAll = () => {
        var newFlag = !!columns.find(x => !x.isVisible);

        var c = columns.map(function (x) {
            x.isVisible = newFlag
            return x;
        });

        setColumns(c);

        localStorage.setItem(pageName + "-" + pageVersion + "-filters", JSON.stringify(c))
    }

    return (
        <div className='m-0'>
            <a
                className={clsx('btn btn-sm btn-flex fw-bold bg-gray-200 btn-color-gray-700 btn-active-color-primary btn-active-light-primary')}
                data-kt-menu-trigger='click'
                data-kt-menu-placement='bottom-end'
                data-kt-menu-static="true"
            >
                <i className='fa fa-columns fs-6 color-gray-700 me-1' />
                Kolon Seçici
            </a>

            <div className='menu menu-sub menu-sub-dropdown w-300px w-md-300px' data-kt-menu='true'>
                <div className='px-7 py-5'>
                    <Box flexDirection="row">
                        <Box justifyContent="center" flexGrow={1}>
                            <div className='fs-5 text-gray-900 fw-bolder'>Kolon Seçici</div>
                        </Box>
                        <Box justifyContent="center">
                            <i style={{ fontSize: '18px' }} data-kt-menu-dismiss='true' className="actionIcon text-gray-900 fa fa-times" />
                        </Box>
                    </Box>
                </div>

                <div className='separator border-gray-200'></div>

                <div className='p-3'>
                    <CustomTextInput placeholder='Ara...' value={searchValue} setValue={setSearchValue} />
                </div>

                <div className='separator border-gray-200'></div>

                <div ref={kapRef} style={{ overflowY: 'auto' }} className='px-7 py-5 h-500px'>
                    <div className="list">
                        {
                            listelenen.map((item, index) =>
                                <div
                                    key={item.name}
                                    ref={el => { satirlar.current[item.name] = el; }}
                                    className={clsx('kolon-secici-satir', surukleme?.ad == item.name && 'kolon-secici-suruklenen')}
                                    style={{ userSelect: 'none' }}
                                >
                                        <Box mb='10px' flexDirection='row'>
                                            <Box mr='10px' justifyContent='center'>
                                                <span
                                                    className='kolon-secici-tutamac'
                                                    title='Sürükleyerek sırasını değiştirin'
                                                    onPointerDown={e => suruklemeyeBasla(e, item.name)}
                                                >
                                                    <i style={{ fontSize: '16px' }} className="text-primary fa fa-bars" />
                                                </span>
                                            </Box>
                                            <Box flexGrow={1} justifyContent='center'>
                                                <div className="form-check form-check-custom form-check-solid d-flex">
                                                    <input onChange={(e) => { setColumnVisibility(item.name) }} className="actionIcon form-check-input" type="checkbox" value="" id={"columnChooser" + index} checked={item.isVisible} />
                                                    <label className="actionIcon form-check-label text-dark" htmlFor={"columnChooser" + index}>
                                                        {item.title}
                                                    </label>
                                                </div>
                                            </Box>
                                        </Box>
                                </div>
                            )
                        }
                    </div>
                </div>

                <div className='separator border-gray-200'></div>

                <div className='px-7 py-5'>
                    <div className='d-flex justify-content-end'>
                        <button
                            style={{ marginRight: '15px' }}
                            onClick={selectOrDeselectAll}
                            className='btn btn-sm btn-light btn-active-light-primary'
                        // data-kt-menu-dismiss='true'
                        >
                            {columns.find(x => !x.isVisible) ? 'Tümünü Seç' : 'Tümünü Kaldır'}
                        </button>

                        <button
                            onClick={() => {
                                localStorage.removeItem(pageName + "-" + pageVersion + "-filters");
                                // Kolon genişlikleri de varsayılana döner (16.10.2026).
                                genislikleriSifirla();
                                setColumns(getColumns())
                            }}
                            className='btn btn-sm btn-light btn-active-light-primary'
                            data-kt-menu-dismiss='true'
                        >
                            Sıfırla
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ColumnChooser;